import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { createPayPalOrder, capturePayPalOrder } from '../lib/paypal';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { RegistryItem, Registry, RSVPResponse } from '../types/registry';
import TransactionBreakdown from './TransactionBreakdown';

interface Props {
  item: RegistryItem;
  registry: Registry;
  onClose: () => void;
  onContribute: () => void;
  rsvpData?: RSVPResponse;
}

export default function GroupGiftModal({ item, registry, onClose, onContribute, rsvpData }: Props) {
  const [contribution, setContribution] = useState({
    name: '',
    email: '',
    amount: 0,
    message: '',
    phone: '',
    address: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [feeSettings, setFeeSettings] = useState({
    platformFee: 0,
    gatewayFee: 0
  });

  const remainingAmount = item.target_amount! - item.collected_amount;
  const progressPercentage = (item.collected_amount / item.target_amount!) * 100;
  const isFullyFunded = remainingAmount <= 0;
  const { user } = useAuth();
  const isOwner = user?.id === registry.user_id;

  useEffect(() => {
    loadFeeSettings();
  }, []);

  useEffect(() => {
    if (rsvpData) {
      setContribution(prev => ({
        ...prev,
        name: rsvpData.name || '',
        email: rsvpData.email || '',
        phone: rsvpData.phone || ''
      }));
    }
  }, [rsvpData]);

  const loadFeeSettings = async () => {
    try {
      const { data: platformFeeData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .maybeSingle();

      const { data: gatewayFeeData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .maybeSingle();

      setFeeSettings({
        platformFee: parseFloat(platformFeeData?.value || '0'),
        gatewayFee: parseFloat(gatewayFeeData?.value || '0')
      });
    } catch (error) {
      console.error('Error loading fee settings:', error);
    }
  };

  const handleShowPayPal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contribution.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!contribution.email.trim()) {
      setError('Email is required');
      return;
    }
    if (contribution.amount <= 0 || contribution.amount > remainingAmount) {
      setError('Please enter a valid contribution amount');
      return;
    }
    setError('');

    try {
      setLoading(true);
      const { data: guestUser, error: guestError } = await supabase
        .from('guest_users')
        .insert([{
          name: contribution.name.trim(),
          email: contribution.email.trim()
        }])
        .select()
        .single();

      if (guestError) throw guestError;
      if (!guestUser) throw new Error('Failed to create guest user');

      setGuestUserId(guestUser.id);
      setShowPayPal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to proceed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-[800px] bg-gray-800/95 backdrop-blur-sm rounded-lg overflow-hidden">
        <div className="bg-gray-900/50 p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl md:text-2xl font-semibold text-white">Group Gift Contribution</h3>
              <p className="mt-2 text-gray-300">Contributing to: {item.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {error && (
            <div className="mb-6 bg-red-900/50 text-red-200 p-4 rounded-md">
              {error}
            </div>
          )}

          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-300">
                <span>Progress: {progressPercentage.toFixed(1)}%</span>
                {isOwner && (
                  <span>{registry.currency} {item.collected_amount.toFixed(2)} of {registry.currency} {item.target_amount!.toFixed(2)}</span>
                )}
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-600 to-primary-400 transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {!showPayPal ? (
              <form onSubmit={handleShowPayPal} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      value={contribution.name}
                      onChange={(e) => setContribution({ ...contribution, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your full name"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={contribution.email}
                      onChange={(e) => setContribution({ ...contribution, email: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your email"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={contribution.phone}
                    onChange={(e) => setContribution({ ...contribution, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your phone number"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                  <input
                    type="text"
                    value={contribution.address}
                    onChange={(e) => setContribution({ ...contribution, address: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your address"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
                  <input
                    type="text"
                    value={contribution.city}
                    onChange={(e) => setContribution({ ...contribution, city: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your city"
                    required
                    disabled={loading}
                  />
                </div>

                {!isFullyFunded && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Contribution Amount ({registry.currency}) *
                    </label>
                    <input
                      type="number"
                      value={contribution.amount || ''}
                      onChange={(e) => {
                        const newAmount = Math.min(Number(e.target.value), remainingAmount);
                        setContribution({ ...contribution, amount: newAmount || 0 });
                      }}
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Enter your contribution amount"
                      required
                      min={1}
                      max={remainingAmount}
                      step="0.01"
                      disabled={loading || remainingAmount <= 0}
                    />
                    {isOwner && (
                      <p className="text-xs text-gray-400 mt-1">
                        Maximum: {registry.currency} {remainingAmount.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message (optional)</label>
                  <textarea
                    value={contribution.message}
                    onChange={(e) => setContribution({ ...contribution, message: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    rows={3}
                    placeholder="Add a personal message with your contribution..."
                    disabled={loading}
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-6 py-3 text-gray-300 hover:text-white bg-gray-700/50 rounded-md hover:bg-gray-600/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || contribution.amount <= 0 || contribution.amount > remainingAmount || remainingAmount <= 0}
                    className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Processing...' : remainingAmount <= 0 ? 'Fully Funded' : 'Contribute'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <p className="text-sm text-gray-300 mb-1">Contributing as</p>
                  <p className="text-white font-medium">{contribution.name}</p>
                  <p className="text-gray-400 text-sm">{contribution.email}</p>
                  <p className="text-gray-400 text-sm mt-1">Amount: {registry.currency} {contribution.amount.toFixed(2)}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <PayPalScriptProvider options={{ clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID, currency: registry.currency === 'LKR' ? 'USD' : registry.currency }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                      createOrder={async () => {
                        setError('');
                        const response = await createPayPalOrder({
                          itemId: item.id,
                          itemName: item.name,
                          originalAmount: contribution.amount,
                          currency: registry.currency,
                          feeResponsibility: registry.fee_responsibility,
                          platformFeePercentage: feeSettings.platformFee,
                          gatewayFeePercentage: feeSettings.gatewayFee,
                          registryId: item.registry_id,
                          registryOwnerId: registry.user_id,
                          payerFirstName: contribution.name.split(' ')[0],
                          payerLastName: contribution.name.split(' ').slice(1).join(' '),
                          payerEmail: contribution.email,
                          guestId: guestUserId || undefined,
                          contributionMessage: contribution.message || undefined,
                          isGroupGift: true,
                        });
                        return response.orderId;
                      }}
                      onApprove={async (data) => {
                        try {
                          await capturePayPalOrder(data.orderID);
                          onContribute();
                        } catch (err: any) {
                          setError(err.message || 'Payment capture failed');
                        }
                      }}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        setError('Payment failed. Please try again.');
                      }}
                      onCancel={() => {
                        setLoading(false);
                      }}
                    />
                  </PayPalScriptProvider>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPayPal(false)}
                  className="w-full px-6 py-3 text-gray-300 hover:text-white bg-gray-700/50 rounded-md hover:bg-gray-600/50 transition-colors"
                >
                  Back to Edit Details
                </button>
              </div>
            )}

            <div className="space-y-6">
              <TransactionBreakdown
                originalAmount={contribution.amount || 0}
                currency={registry.currency}
                showDetailed={false}
                feeResponsibility={registry.fee_responsibility}
                platformFeePercentage={feeSettings.platformFee}
                gatewayFeePercentage={feeSettings.gatewayFee}
              />
              <div className="bg-gray-800/30 p-3 rounded-md">
                <p className="text-sm text-gray-300 text-center">
                  Your contribution is secure and processed by PayPal
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
